import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import styled, {
  createGlobalStyle,
  ThemeProvider as StyledThemeProvider,
} from "styled-components";
import Dashboard from "./pages/Dashboard";
import Watchlist from "./pages/Watchlist";
import Jobs from "./pages/Jobs";
import Sidebar from "./components/Sidebar";
import ResearchPage from "./pages/ResearchPage";
import { ThemeProvider, ThemeVars, useTheme } from "./theme/ThemeContext";
import { ToastProvider } from "./components/ToastProvider";
import ActivityTray, { ActivityProvider } from "./components/ActivityTray";
import Settings from "./pages/Settings";
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import CheckoutPage from "./pages/CheckoutPage";
import PlansPage from "./pages/PlansPage";
import LegalPage from "./pages/LegalPage";
import FindCandidatesPage from "./pages/FindCandidatesPage";
import ManualSearchPage from "./pages/ManualSearchPage";
import Outreach from "./pages/Outreach";
import { useAuth } from "./context/AuthContext";
import GetStartedPage from "./pages/GetStartedPage";
import GuidedTour from "./components/GuidedTour";
import { Navigate } from "react-router-dom";
import { setAuthFailureCallback } from "./utils/authFetch";
import { releaseBootSplash } from "./utils/bootSplash";
import { FindNotificationProvider } from "./context/FindNotificationContext";
import GoogleAnalytics from "./components/GoogleAnalytics";
import { PlanGate } from "./components/PlanLock";
import WelcomeScreen from "./components/WelcomeScreen";
import TrialEndedWall from "./components/TrialEndedWall";

const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    font-family: Inter, system-ui, -apple-system, "Segoe UI", sans-serif;
    background: ${({ theme }) => theme.bg};
    color: ${({ theme }) => theme.ink};
    overflow-x: hidden;
    /* 100% not 100vw: 100vw includes the scrollbar gutter, so every page sat
       ~15px wider than the viewport and got clipped on the right. */
    width: 100%;
    box-sizing: border-box;
    transition: background 0.2s, color 0.2s;
  }
  
  @media (max-width: 450px) {
    html {
      font-size: 14px;
    }
    
    body {
      -webkit-text-size-adjust: 100%;
      -moz-text-size-adjust: 100%;
      text-size-adjust: 100%;
    }
    
    /* Ensure touch targets are at least 44px for accessibility */
    button, a, input, select, textarea {
      min-height: 44px;
    }

    /* Checkboxes and radios sit inline with their label; 44px tall breaks the row */
    input[type="checkbox"], input[type="radio"] {
      min-height: 0;
    }

    /* iOS Safari zooms the page when a field under 16px takes focus */
    input:not([type="checkbox"]):not([type="radio"]), select, textarea {
      font-size: 16px !important;
    }
    
    /* Wide media only — never layout containers. A universal max-width also
       caps full-bleed wrappers that expand via negative side margins, pinning
       them flush left and leaving the reclaimed padding as dead space on the
       right. Horizontal scrolling is already prevented by overflow-x above. */
    img, video, canvas, svg, iframe, table, pre {
      max-width: 100%;
    }
  }
  
  @media (max-width: 390px) {
    html {
      font-size: 13px;
    }
    
    /* Slightly smaller touch targets on very small screens but still accessible */
    button, a {
      min-height: 40px;
    }
    
    input:not([type="checkbox"]):not([type="radio"]), select, textarea {
      min-height: 40px;
    }
  }
`;

const Layout = styled.div`
  display: flex;
  /* dvh so a real phone's URL bar can't push the document past the viewport. */
  min-height: 100vh;
  min-height: 100dvh;
  background: ${({ theme }) => theme.bg};

  /* Desktop: gutter matches sidebar so main’s rounded corners read clearly */
  @media (min-width: 1025px) {
    background: ${({ theme }) => theme.surface};
  }
`;

const MainContent = styled.main`
  flex: 1;
  /* Flex items default to min-width:auto, which blocks flex:1 from shrinking
     below the page's min-content width — that is what pushes wide pages past
     the right edge. Every page renders inside this element. */
  min-width: 0;
  padding: 2.5rem 2.5rem 2.5rem 2.5rem;
  margin-left: ${({ $sidebarOpen }) =>
    $sidebarOpen ? "var(--sidebar-offset, 18rem)" : "0"};
  transition:
    margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 0.2s ease;
  background: ${({ theme }) => theme.bg};
  min-height: 100dvh;
  box-sizing: border-box;

  @media (min-width: 1025px) {
    border-top-left-radius: ${({ $sidebarOpen }) => ($sidebarOpen ? "20px" : "0")};
    border-top-right-radius: ${({ $sidebarOpen }) => ($sidebarOpen ? "20px" : "0")};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  @media (max-width: 1024px) {
    /* Keep top-row content clear of the fixed mobile menu button. */
    padding: 4.75rem 1.5rem 1.5rem 1.5rem;
    margin-left: 0;
    border-radius: 0;
  }
  
  @media (max-width: 768px) {
    padding: 4.25rem 1rem 1rem 1rem;
    margin-left: 0;
  }
  
  @media (max-width: 450px) {
    padding: 4rem 0.75rem 0.75rem 0.75rem;
  }
  
  @media (max-width: 390px) {
    padding: 3.8rem 0.5rem 0.5rem 0.5rem;
  }
`;

function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  React.useEffect(() => {
    if (navType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
}

function PathTracker() {
  const location = useLocation();
  const prevPathRef = React.useRef(location.pathname);

  React.useEffect(() => {
    const prevPath = prevPathRef.current;
    const nextPath = location.pathname;
    if (nextPath !== prevPath) {
      try {
        sessionStorage.setItem("prev-pathname", prevPath);
      } catch (_) {
        // ignore storage errors
      }
      prevPathRef.current = nextPath;
    }
  }, [location.pathname]);

  return null;
}

// Longest the boot splash waits for WelcomeScreen's decision before the app shows anyway.
const BOOT_SPLASH_MAX_MS = 6000;
// Pages rendered outside the signed-in shell (no WelcomeScreen to release the splash).
const PUBLIC_PATHS = ["/terms", "/privacy-policy"];

function AppInner() {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const { isAuthenticated, isLoading, login, features } = useAuth();

  // When session expires (API returns 401), redirect to backend login with reauth to force fresh login
  React.useEffect(() => {
    setAuthFailureCallback(() => login({ reauth: true }));
    return () => setAuthFailureCallback(null);
  }, [login]);

  // The boot splash (public/index.html) stays up through the session check and, for a signed-in user, until
  // WelcomeScreen knows whether this login gets the welcome and releases it; a safety timer covers a hung request.
  React.useEffect(() => {
    if (isLoading) return undefined;
    // Signed out, or on a public page outside the shell: nothing else will release it, so release now.
    if (!isAuthenticated || PUBLIC_PATHS.includes(window.location.pathname.replace(/\/+$/, ""))) {
      releaseBootSplash();
      return undefined;
    }
    const timer = setTimeout(releaseBootSplash, BOOT_SPLASH_MAX_MS);
    return () => clearTimeout(timer);
  }, [isLoading, isAuthenticated]);

  // Auto-close sidebar on tablet and smaller screens
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    // Check initial screen size
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // The boot splash is still up while auth state loads; nothing to draw under it yet.
  if (isLoading) return <GlobalStyle />;
  return (
    <Router>
      <GlobalStyle />
      <ScrollToTop />
      <PathTracker />
      <GoogleAnalytics />
      <Routes>
        {/* Public pages, reachable whether or not the user is signed in */}
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy-policy" element={<LegalPage doc="privacy" />} />
        <Route path="/pricing" element={isAuthenticated ? <Navigate to="/plans" replace /> : <PricingPage />} />
        <Route path="/*" element={!isAuthenticated ? (
        <Routes>
          <Route path="/login" element={<LandingPage />} />
          <Route path="/signup" element={<LandingPage />} />
          <Route path="/*" element={<LandingPage />} />
        </Routes>
      ) : (
        <FindNotificationProvider>
          <>
            <ActivityProvider>
            <Layout>
              <Sidebar />
              <MainContent $sidebarOpen={sidebarOpen}>
              <WelcomeScreen />
              <TrialEndedWall />
              <GuidedTour />
              <ActivityTray />
              <Routes>
                <Route
                  path="/"
                  element={<Dashboard />}
                />
                <Route path="/get-started" element={<GetStartedPage />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/plans" element={<PlansPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route
                  path="/jobs"
                  element={
                    <PlanGate feature="jobs">
                      <Jobs />
                    </PlanGate>
                  }
                />
                <Route
                  path="/watchlist"
                  element={
                    <PlanGate feature="jobs" page="watchlist">
                      <Watchlist />
                    </PlanGate>
                  }
                />
                <Route
                  path="/outreach"
                  element={
                    <PlanGate feature="outreach">
                      <Outreach />
                    </PlanGate>
                  }
                />
                {/* Manual search is behind FEATURE_MANUAL_SEARCH: off, the path falls through to the dashboard. */}
                {features?.manualSearch && <Route path="/find-candidates/manual" element={<ManualSearchPage />} />}
                <Route path="/find-candidates/history/:id" element={<FindCandidatesPage />} />
                <Route path="/find-candidates/history" element={<FindCandidatesPage />} />
                <Route path="/find-candidates" element={<FindCandidatesPage />} />
                <Route path="/research/:companyName" element={<PlanGate feature="research"><ResearchPage /></PlanGate>} />
                <Route path="/research" element={<PlanGate feature="research"><ResearchPage /></PlanGate>} />
                {/* Signed in, any other path (/login, /signup, a stale link): the dashboard, not a blank page */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </MainContent>
            </Layout>
            </ActivityProvider>
          </>
          </FindNotificationProvider>
      )} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ThemeConsumerWrapper />
    </ThemeProvider>
  );
}

function ThemeConsumerWrapper() {
  const theme = useTheme();
  return (
    <StyledThemeProvider theme={theme}>
      {/* Publishes the palette as CSS custom properties on :root — must sit
          above everything that reads var(--*), i.e. the whole app. */}
      <ThemeVars />
      <ToastProvider>
        <AppInner />
      </ToastProvider>
    </StyledThemeProvider>
  );
}

export default App;
